import React, { useMemo, useState } from 'react'
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useLocation,
} from 'react-router-dom'
import axios from '../../../config/axios'
import RegisterVehicleModal from './Vehicles/RegisterVehicleModal/index'
import { Content } from './style'

import settings from '../../../assets/equipment/TableMovementHeader/settings.svg'
import truck from '../../../assets/equipment/TableMovementHeader/truck.svg'

import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { Header, Filter } from '../../../components/SubHeaderFilter/types'
import { PageHeader } from '../../../components/StandardPage'

interface TypeMainOutlet {
  search: string
}

export interface EquipmentOutletType {
  tabIndex: number
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
  refresh: boolean
  search: string
  appliedFilters: Filter[]
}

interface PeriodicMaintenanceData {
  maintenanceName: string
  periodicityValue: number | ''
  periodicityUnit: string
}

interface VehicleDataToSend {
  plate: string
  model: string
  year: number
  capacity: string
  bodyType: string
  restriction: string | null
  periodicMaintenances: PeriodicMaintenanceData[]
}

const machineHeaders: Header[] = [
  { key: 'name', label: 'Nome da Máquina', type: 'string' },
  { key: 'model', label: 'Modelo', type: 'string' },
  { key: 'costPerHour', label: 'Custo por Hora', type: 'currency' },
  { key: 'lastMaintenance', label: 'Última Manutenção', type: 'date' },
  { key: 'nextMaintenance', label: 'Próxima Manutenção', type: 'date' },
]

const vehicleHeaders: Header[] = [
  { key: 'plate', label: 'Placa', type: 'string' },
  { key: 'model', label: 'Modelo', type: 'string' },
  { key: 'year', label: 'Ano', type: 'number' },
  { key: 'capacity', label: 'Capacidade', type: 'string' },
  { key: 'bodyType', label: 'Tipo de Carroceria', type: 'string' },
  { key: 'restriction', label: 'Restrição', type: 'string' },
]

const equipmentTabs = [
  { key: '', label: 'Máquinas', icon: settings },
  { key: 'vehicles', label: 'Veículos', icon: truck },
]

const EquipmentContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { search } = useOutletContext<TypeMainOutlet>()
  const [refresh, setRefresh] = useState(false)

  const tabIndex = location.pathname.includes('/panel/equipment/vehicles') ? 1 : 0

  const [isRegisterVehicleModalOpen, setIsRegisterVehicleModalOpen] =
    useState(false)

  const handleCloseRegisterVehicleModal = () => {
    setIsRegisterVehicleModalOpen(false)
    setRefresh((prev) => !prev)
  }

  const handleSaveRegisterVehicleModal = async (
    vehicleData: VehicleDataToSend,
  ): Promise<boolean> => {
    const token = localStorage.getItem('token')
    if (!token) {
      alert('Sessão expirada ou não logado. Faça login novamente.')
      return false
    }
    if (!vehicleData.plate || !vehicleData.model || !vehicleData.year) {
      alert('Por favor, preencha os campos obrigatórios: Placa, Modelo e Ano.')
      return false
    }

    if (vehicleData.periodicMaintenances) {
      for (const item of vehicleData.periodicMaintenances) {
        if (
          !item.maintenanceName ||
          typeof item.periodicityValue !== 'number' ||
          item.periodicityValue <= 0 ||
          !item.periodicityUnit
        ) {
          alert(
            `Preencha todos os campos obrigatórios para cada manutenção periódica (Nome, Valor da Periodicidade e Unidade).`,
          )
          return false
        }
      }
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment/vehicles`,
        vehicleData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.status === 201 || response.status === 200) {
        alert('Veículo cadastrado com sucesso!')
        handleCloseRegisterVehicleModal()
        setRefresh((prev) => !prev)
        return true
      } else {
        alert(
          `Erro ao cadastrar veículo: ${
            response.statusText || 'Resposta inesperada do servidor.'
          }`,
        )
        return false
      }
    } catch (error) {
      console.error('Erro ao salvar veículo:', error)
      let errorMessage = 'Ocorreu um erro inesperado ao salvar o veículo.'
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message
        if (error.response?.status === 401) {
          errorMessage = 'Não autorizado. Por favor, faça login novamente.'
        } else if (error.response?.status === 400) {
          errorMessage = `Dados inválidos: ${
            error.response?.data?.details ||
            error.response?.data?.message ||
            'Verifique os campos preenchidos.'
          }`
        }
      }
      alert(`Erro ao cadastrar veículo: ${errorMessage}`)
      return false
    }
  }

  const machinesFilters = usePersistedFilters('equipment_machines')
  const vehiclesFilters = usePersistedFilters('equipment_vehicles')

  const activeFiltersState = tabIndex === 0 ? machinesFilters : vehiclesFilters
  const activeHeaders = tabIndex === 0 ? machineHeaders : vehicleHeaders

  const outletContext: EquipmentOutletType = useMemo(
    () => ({
      tabIndex,
      setTabIndex: () => {},
      refresh,
      search,
      appliedFilters: activeFiltersState.appliedFilters,
    }),
    [tabIndex, refresh, search, activeFiltersState.appliedFilters],
  )

  const shouldShowSubHeader =
    location.pathname === '/panel/equipment' ||
    location.pathname === '/panel/equipment/' ||
    location.pathname === '/panel/equipment/vehicles' ||
    location.pathname === '/panel/equipment/vehicles/'

  return (
    <>
      {shouldShowSubHeader && (
        <PageHeader
          title='Equipamentos'
          tabs={equipmentTabs}
          activeTab={tabIndex === 1 ? 'vehicles' : ''}
          onTabChange={(key) => navigate(`/panel/equipment/${key}`)}
          onReload={() => setRefresh((prev) => !prev)}
          filterHeaders={activeHeaders}
          appliedFilters={activeFiltersState.appliedFilters}
          onApplyFilter={activeFiltersState.handleApplyFilter}
          onRemoveFilter={activeFiltersState.handleRemoveFilter}
          onClearAllFilters={activeFiltersState.handleClearAll}
          createButton={{
            label: tabIndex === 0 ? 'Cadastrar máquina' : 'Cadastrar veículo',
            onClick: () =>
              tabIndex === 0
                ? navigate('/panel/equipment/register')
                : setIsRegisterVehicleModalOpen(true),
          }}
        />
      )}
      <Content>
        <Outlet context={outletContext} />
      </Content>
      <RegisterVehicleModal
        isOpen={isRegisterVehicleModalOpen}
        onClose={handleCloseRegisterVehicleModal}
        onSave={handleSaveRegisterVehicleModal}
      />
    </>
  )
}

export default EquipmentContent
